import { http } from '../lib/http'

/* A buyer's own records, as the API serves them.

   Not one of these calls takes an id. Which person is being asked about
   comes from the token — the API reads it there and answers about nobody
   else — so there is no URL on these pages that could be edited into
   somebody else's bookings.

   The shapes are the console's own, because they are the same records. A
   buyer ringing up about a receipt should be reading the figure the person
   answering the phone is reading. */

export type AccountCustomer = {
  id: string
  fullName: string
  phone: string
  email: string
  address: string
  city: string
  /** When the showroom first filed them. */
  since: string
}

export type AccountSummary = {
  bookings: number
  /** Orders neither completed nor cancelled. */
  open: number
  cars: number
  /** How many of those cars are on a bank plan. */
  plans: number
}

export type Account = {
  user: {
    id: string
    username: string
    fullName: string
    email: string
    phone: string
    roleName: string
    permissions: string[]
  }
  /** Null only if a sign-in was somehow left unlinked from a customer file. */
  customer: AccountCustomer | null
  summary: AccountSummary | null
}

export const getAccount = () => http.get<Account>('/account/me').then((response) => response.data)

export type AccountChanges = Partial<Pick<AccountCustomer, 'phone' | 'email' | 'address' | 'city'>>

export const updateAccount = (changes: AccountChanges) =>
  http.patch<AccountCustomer>('/account/me', changes).then((response) => response.data)

export const changePassword = (currentPassword: string, password: string) =>
  http
    .post<{ ok: true }>('/account/password', { currentPassword, password })
    .then((response) => response.data)

/* ---------- orders ---------- */

export type Payment = {
  id: string
  amount: number
  method: string
  reference: string
  note: string
  paidAt: string
  recordedAt: string
}

/** The car as it was at the moment of booking. A booking keeps its own copy,
    so a record edited later never rewrites what somebody actually ordered. */
export type BookedVehicle = {
  id: string
  plateNumber?: string
  title: string
  brand: string
  model: string
  variant: string
  year: number
  price: number
  image: string
  colour: string
  colourHex: string
}

export type AccountBooking = {
  id: string
  reference: string
  vehicleId: string
  vehicle: BookedVehicle | null
  agreedPrice: number
  payments: Payment[]
  status: string
  createdAt: string
  updatedAt: string
  /** Added up by the API: what has been paid, what it comes to, what is left. */
  paid: number
  total: number
  balance: number
}

export const getMyBookings = () =>
  http.get<AccountBooking[]>('/account/bookings').then((response) => response.data)

/* ---------- cars ---------- */

/** A car in this buyer's name, joined to the immutable Sale ledger snapshot.
    Cost and realised showroom margin never leave the console. */
export type AccountVehicle = {
  id: string
  title: string
  brand: string
  model: string
  variant: string
  year: number
  colour: string
  colourHex: string
  image: string
  images: string[]
  /** What this buyer agreed to pay. Never replaced by the live variant quote. */
  soldPrice: number | null
  soldAt: string | null
  status: string
  plateNumber?: string
}

export const getMyVehicles = () =>
  http.get<AccountVehicle[]>('/account/vehicles').then((response) => response.data)

/* ---------- the bank calendar ---------- */

export type Tally = { count: number; amount: number }

/** One month of a plan. Only 'paid' is stored; the other three are the
    calendar read against today. */
export type ScheduleRow = {
  sequence: number
  of: number
  dueOn: string
  month: string
  amount: number
  status: 'paid' | 'overdue' | 'due' | 'upcoming'
  /** 'sultan-motors' or 'client' — often the buyer themselves. */
  paidBy: string
  paidOn: string
  paidAmount: number
  note: string
  recordedAt: string
}

export type Plan = {
  bookedOn: string
  years: number
  months: number
  monthly: number
  total: number
  endsOn: string
  scheduled: Tally
  paid: Tally
  byUs: Tally
  byClient: Tally
  overdue: Tally
  unpaid: Tally
  due: Tally
  nextDue: { sequence: number; dueOn: string; amount: number } | null
  settled: boolean
  /** 0 to 1 — months paid over months in the plan. */
  progress: number
}

export type AccountInstalment = {
  vehicle: { id: string; title: string; year: number; image: string }
  plan: Plan
  schedule: ScheduleRow[]
}

export const getMyInstalments = () =>
  http.get<AccountInstalment[]>('/account/instalments').then((response) => response.data)
